import { Button, type ButtonProps } from "@rulebase/core/components";
import { IconCheckmark1, IconSquareBehindSquare1 } from "central-icons";
import { useEffect, useState } from "react";

export type CopyButtonProps = Omit<ButtonProps, "onClick" | "icon"> & {
  /** Resolved when clicked, so a transcript can be fetched on demand. */
  getText: () => string | Promise<string>;
  copiedLabel?: string;
};

/**
 * Confirms in place — the label and icon become "Copied" with a checkmark for a
 * moment — rather than raising a toast for something the user just did and is
 * already looking at.
 */
export function CopyButton({
  getText,
  copiedLabel = "Copied",
  children,
  ...rest
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  async function onClick() {
    setCopying(true);
    try {
      const text = await getText();
      if (!text) return;
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch (error) {
      // The label simply never changes, which reads as "that didn't work".
      console.error("Couldn't copy to the clipboard", error);
    } finally {
      setCopying(false);
    }
  }

  return (
    <Button
      onClick={onClick}
      loading={copying}
      icon={copied ? IconCheckmark1 : IconSquareBehindSquare1}
      {...rest}
    >
      {copied ? copiedLabel : children}
    </Button>
  );
}
